import { useEffect, useState } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation, useNavigate } from 'react-router-dom';

import './App.css';
import AppBootstrapLoader from './components/AppBootstrapLoader';
import PublicScrollRestoration from './components/routing/PublicScrollRestoration';
import RoleRoute from './components/routing/RoleRoute';
import LoginPage from './pages/Auth/LoginPage';
import ReferralInscriptionRedirect from './pages/Auth/ReferralInscriptionRedirect';
import ForgotPasswordPage from './pages/Auth/ForgotPasswordPage';
import VerifyPasswordResetPage from './pages/Auth/VerifyPasswordResetPage';
import ResetPasswordPage from './pages/Auth/ResetPasswordPage';
import AdminAcademiaPage from './pages/Admin/AdminAcademiaPage';
import ApprenantAcademiaPage from './pages/Apprenant/ApprenantAcademiaPage';
import FormateurAcademiaPage from './pages/Formateur/FormateurAcademiaPage';
import PartenaireAcademiaPage from './pages/Partenaire/PartenaireAcademiaPage';
import AmbassadeurAcademiaPage from './pages/Ambassadeur/AmbassadeurAcademiaPage';
import AcademiaHomePage from './pages/Public/AcademiaHomePage';
import PublicExcellencePage from './pages/Public/PublicExcellencePage';
import FiliereTrackPage from './pages/Public/FiliereTrackPage';
import FiliereDetailPage from './pages/Public/FiliereDetailPage';
import PublicLegalPage from './pages/PublicLegalPage';
import { ROLE_ADMIN, ROLE_APPRENANT, ROLE_FORMATEUR, ROLE_MODERATEUR, ROLE_PARTENAIRE, ROLE_AMBASSADEUR } from './constants/roles';
import { authService } from './services/authService';
import { connectUserProfileSocket } from './services/userProfileSocket';
import {
  canAccessAdminPortal,
  canAccessApprenantPortal,
  canAccessFormateurPortal,
  getHomePathForRole,
  getPortalRedirectForPath,
  resolvePostAuthPath,
} from './utils/authRouting';
import { applyPageMeta, resolvePageMetaFromPath } from './utils/pageMeta';

function ProtectedRoute({ isAllowed, redirectTo = '/login' }) {
  if (!isAllowed) {
    return <Navigate to={redirectTo} replace />;
  }
  return <Outlet />;
}

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const [authState, setAuthState] = useState(() => authService.getStoredAuth());

  const [isBootstrapping, setIsBootstrapping] = useState(true);

  useEffect(() => {
    if (location.pathname.startsWith('/filieres/')) {
      return;
    }
    applyPageMeta(resolvePageMetaFromPath(location.pathname));
  }, [location.pathname]);

  useEffect(() => {
    if (!location.pathname.startsWith('/admin')) {
      return;
    }
    document.body.style.overflow = '';
    document.body.style.pointerEvents = '';
    document.documentElement.style.overflow = '';
    document.documentElement.style.pointerEvents = '';
  }, [location.pathname]);

  useEffect(() => {
    let isMounted = true;

    const bootstrapSession = async () => {
      const storedState = authService.getStoredAuth();
      if (!storedState.tokens?.accessToken) {
        if (isMounted) setIsBootstrapping(false);
        return;
      }
      try {
        const profile = await authService.getCurrentUser();
        if (isMounted) {
          setAuthState((current) => ({ ...current, user: profile }));
        }
      } catch {
        authService.clearAuth();
        if (isMounted) {
          setAuthState(authService.getStoredAuth());
        }
      } finally {
        if (isMounted) setIsBootstrapping(false);
      }
    };

    void bootstrapSession();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (isBootstrapping || !authState.user) {
      return;
    }
    if (location.pathname === '/login') {
      navigate(resolvePostAuthPath(authState.user.role, authState.user), { replace: true });
      return;
    }
    const portalRedirect = getPortalRedirectForPath(
      location.pathname,
      authState.user.role,
      authState.user,
    );
    if (portalRedirect) {
      navigate(portalRedirect, { replace: true });
    }
  }, [isBootstrapping, authState.user, location.pathname, navigate]);

  const handleAuthSuccess = (nextState) => {
    setAuthState(nextState);
    const user = nextState?.user;
    if (!user) {
      return;
    }
    navigate(resolvePostAuthPath(user.role, user), { replace: true });
  };

  const handleUserUpdated = (updatedUser) => {
    if (!updatedUser) return;
    setAuthState((current) => {
      const next = { ...current, user: updatedUser };
      localStorage.setItem('onego_user', JSON.stringify(updatedUser));
      return next;
    });
  };

  useEffect(() => {
    if (!authState.user?.id || !authState.user?.role) {
      return undefined;
    }

    return connectUserProfileSocket({
      userId: authState.user.id,
      role: authState.user.role,
      onProfileUpdated: async () => {
        try {
          const profile = await authService.getCurrentUser();
          if (profile) {
            handleUserUpdated(profile);
          }
        } catch {
          // Ignore transient network errors; session bootstrap handles hard failures.
        }
      },
    });
  }, [authState.user?.id, authState.user?.role]);

  const handleLogout = async () => {
    try {
      await authService.logout();
    } catch {
      // Must clear local session even if API logout fails.
    } finally {
      authService.clearAuth();
      setAuthState(authService.getStoredAuth());
      navigate('/', { replace: true });
    }
  };

  if (isBootstrapping) {
    return <AppBootstrapLoader />;
  }

  const isAuthenticated = Boolean(authState.user);
  const userRole = authState.user?.role;
  const isAdmin = canAccessAdminPortal(userRole);
  const spaceLabel = isAdmin
    ? 'Administration'
    : canAccessFormateurPortal(userRole)
      ? 'Espace formateur'
      : userRole === ROLE_AMBASSADEUR
        ? 'Espace ambassadeur'
        : canAccessApprenantPortal(userRole)
          ? 'Mon espace'
          : 'Mon compte';
  const goToSpace = () => navigate(getHomePathForRole(userRole, authState.user));

  return (
    <>
      <PublicScrollRestoration />
      <Routes>
      <Route
        path="/filieres/:trackSlug/:filiereSlug"
        element={
          <FiliereDetailPage
            isAuthenticated={isAuthenticated}
            isAdmin={isAdmin}
            spaceLabel={spaceLabel}
            onGoToSpace={goToSpace}
          />
        }
      />
      <Route
        path="/filieres/:trackSlug"
        element={
          <FiliereTrackPage
            isAuthenticated={isAuthenticated}
            isAdmin={isAdmin}
            spaceLabel={spaceLabel}
            onGoToSpace={goToSpace}
          />
        }
      />
      <Route
        path="/"
        element={
          <AcademiaHomePage
            isAuthenticated={isAuthenticated}
            isAdmin={isAdmin}
            spaceLabel={spaceLabel}
            onGoToSpace={goToSpace}
          />
        }
      />
      <Route path="/login" element={<LoginPage onSuccess={handleAuthSuccess} />} />
      <Route path="/inscription" element={<ReferralInscriptionRedirect />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/verify-password-reset" element={<VerifyPasswordResetPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/legal/cgu" element={<PublicLegalPage type="cgu" />} />
      <Route path="/legal/confidentialite" element={<PublicLegalPage type="confidentialite" />} />
      <Route
        path="/excellence"
        element={
          <PublicExcellencePage
            isAuthenticated={isAuthenticated}
            isAdmin={isAdmin}
            spaceLabel={spaceLabel}
            onGoToSpace={goToSpace}
          />
        }
      />

      <Route element={<ProtectedRoute isAllowed={isAuthenticated} redirectTo="/login" />}>
        <Route
          element={
            <RoleRoute
              allowedRoles={[ROLE_ADMIN, ROLE_MODERATEUR]}
              userRole={userRole}
              redirectTo={getHomePathForRole(userRole)}
            />
          }
        >
          <Route
            path="/admin/*"
            element={
              <AdminAcademiaPage user={authState.user} onLogout={handleLogout} onUserUpdated={handleUserUpdated} />
            }
          />
        </Route>

        <Route
          element={
            <RoleRoute allowedRoles={[ROLE_APPRENANT]} userRole={userRole} redirectTo={getHomePathForRole(userRole)} />
          }
        >
          <Route
            path="/app/*"
            element={
              <ApprenantAcademiaPage user={authState.user} onLogout={handleLogout} onUserUpdated={handleUserUpdated} />
            }
          />
        </Route>

        <Route
          element={
            <RoleRoute allowedRoles={[ROLE_FORMATEUR]} userRole={userRole} redirectTo={getHomePathForRole(userRole)} />
          }
        >
          <Route
            path="/formateur/*"
            element={
              <FormateurAcademiaPage
                user={authState.user}
                onLogout={handleLogout}
                onUserUpdated={handleUserUpdated}
              />
            }
          />
        </Route>

        <Route
          element={
            <RoleRoute
              allowedRoles={[ROLE_PARTENAIRE]}
              userRole={userRole}
              redirectTo={getHomePathForRole(userRole)}
            />
          }
        >
          <Route
            path="/partenaire/*"
            element={<PartenaireAcademiaPage user={authState.user} onLogout={handleLogout} />}
          />
        </Route>

        <Route
          element={
            <RoleRoute
              allowedRoles={[ROLE_AMBASSADEUR]}
              userRole={userRole}
              redirectTo={getHomePathForRole(userRole, authState.user)}
            />
          }
        >
          <Route
            path="/ambassadeur/*"
            element={
              <AmbassadeurAcademiaPage
                user={authState.user}
                onLogout={handleLogout}
                onUserUpdated={handleUserUpdated}
              />
            }
          />
        </Route>
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

export default App;
